import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useWindowWidth from '../../../hooks/useWindowWidth';
import clsx from 'clsx';

const SecurityCodeInputField = ({ onChange, value }) => {
    const inputs = useRef([]);
    const [values, setValues] = useState(Array(6).fill(''));
    const [focusedIndex, setFocusedIndex] = useState(null);
    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 375;
    
    // Use a ref to track if we're handling an internal change
    const isInternalChange = useRef(false);

    // Sync state when external value changes
    useEffect(() => {
        // Skip if this is from our own internal change
        if (isInternalChange.current) {
            isInternalChange.current = false;
            return;
        }
        
        const val = (value || '').slice(0, 6);
        const digits = val.split('');
        const newValues = Array(6).fill('');

        digits.forEach((digit, index) => {
            newValues[index] = digit;
            if (inputs.current[index]) {
                inputs.current[index].value = digit;
            }
        });

        setValues(newValues);
    }, [value]);

    const handleChange = (e, index) => {
        const digit = e.target.value.replace(/\D/g, '')[0];
        if (!digit) return;

        const newValues = [...values];
        newValues[index] = digit;
        isInternalChange.current = true;
        setValues(newValues);

        const combined = newValues.join('');
        onChange?.(combined);

        const nextIndex = index + 1;
        if (inputs.current[nextIndex]) {
            inputs.current[nextIndex].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key !== 'Backspace') return;

        const newValues = [...values];

        if (!e.target.value && index > 0) {
            newValues[index - 1] = '';
            isInternalChange.current = true;
            setValues(newValues);
            inputs.current[index - 1].value = '';
            inputs.current[index - 1].focus();
        } else {
            newValues[index] = '';
            isInternalChange.current = true;
            setValues(newValues);
            e.target.value = '';
        }

        onChange?.(newValues.join(''));
    };

    const handlePaste = e => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!pasted) return;

        const digits = pasted.split('');
        const newValues = Array(6).fill('');

        digits.forEach((digit, i) => {
            newValues[i] = digit;
            if (inputs.current[i]) {
                inputs.current[i].value = digit;
            }
        });
        isInternalChange.current = true;
        
        setValues(newValues);
        onChange?.(newValues.join(''));

        const nextIndex = Math.min(digits.length, 5);
        inputs.current[nextIndex]?.focus();
    };

    const handleFocus = (index) => {
        setFocusedIndex(index);
    };

    const handleBlur = () => {
        setFocusedIndex(null);
    };

    return (
        <div className={`flex justify-center ${isMobile ? 'gap-[4px]' : 'gap-2'}`}>
            {[0, 1, 2, 3, 4, 5].map(i => (
                <React.Fragment key={i}>
                    {i === 3 && (
                        <div className={`text-[#A0A0AB] font-medium flex items-center ${isMobile ? 'text-3xl px-[1px]' : 'text-6xl px-1'}`}>
                            -
                        </div>
                    )}
                    <input
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        placeholder='0'
                        className={clsx(`
                            w-full h-[72px] text-text text-center text-[52px] font-medium border rounded-lg
                            ${isMobile ? 'min-w-[30px] max-w-[42px]' : 'min-w-[40px] max-w-[72px]'}
                            ${focusedIndex === i || values[i] ? 'bg-modal-bg' : 'bg-card-bg'}
                            placeholder-[#A0A0AB]
                        `)}
                        ref={el => (inputs.current[i] = el)}
                        onChange={e => handleChange(e, i)}
                        onKeyDown={e => handleKeyDown(e, i)}
                        onPaste={i === 0 ? handlePaste : undefined}
                        onFocus={() => handleFocus(i)}
                        onBlur={handleBlur}
                        autoComplete="off"
                        value={values[i]}
                    />
                </React.Fragment>
            ))}
        </div>
    );
};

SecurityCodeInputField.propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
};

export default SecurityCodeInputField;